<script setup>
import { ref, watch, computed, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Switch from 'next/switch/Switch.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import DurationInput from 'next/input/DurationInput.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

const { t } = useI18n();

const getDurationUnit = duration => {
  if (duration && duration % (24 * 60) === 0) return DURATION_UNITS.DAYS;
  if (duration && duration % 60 === 0) return DURATION_UNITS.HOURS;
  return DURATION_UNITS.MINUTES;
};

const createRule = ({
  id = `follow-up-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  duration = 10,
  message = '',
  send_csat = false,
} = {}) => ({
  id,
  duration,
  unit: getDurationUnit(duration),
  message,
  send_csat: !!send_csat,
});

const CONVERSATION_STATUSES = ['open', 'pending', 'resolved', 'snoozed'];

const normalizeStatuses = statuses => {
  if (!Array.isArray(statuses) || !statuses.length) return ['open'];

  const validStatuses = statuses.filter(status =>
    CONVERSATION_STATUSES.includes(status)
  );
  return validStatuses.length ? validStatuses : ['open'];
};

const normalizeLabels = (labels, legacyLabel = '') =>
  [...new Set([...(Array.isArray(labels) ? labels : []), legacyLabel])]
    .map(label => String(label || '').trim())
    .filter(Boolean);

const createGroup = ({
  id = `follow-up-group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  inbox_id = '',
  label = '',
  labels = [],
  exclude_labels = [],
  include_unlabeled = false,
  collapsed = false,
  enabled = true,
  conversation_statuses = ['open'],
  rules = [],
} = {}) => ({
  id,
  inbox_id: inbox_id ? String(inbox_id) : '',
  labels: normalizeLabels(labels, label),
  exclude_labels: normalizeLabels(exclude_labels),
  include_unlabeled: !!include_unlabeled,
  collapsed: !!collapsed,
  enabled: enabled !== false,
  conversation_statuses: normalizeStatuses(conversation_statuses),
  rules: rules.map(rule => createRule(rule)),
});

const followUpGroups = ref([createGroup()]);
const isEnabled = ref(false);
const isSubmitting = ref(false);
const followUpDialogRef = ref(null);
const editingFollowUpGroupId = ref(null);
const editingFollowUpRuleId = ref(null);

const followUpForm = reactive({
  duration: 10,
  unit: getDurationUnit(10),
  message: '',
  send_csat: false,
});

const { currentAccount, updateAccount } = useAccount();
const store = useStore();
const inboxes = useMapGetter('inboxes/getAllInboxes');
const labels = useMapGetter('labels/getLabels');

const inboxOptions = computed(() => [
  {
    value: '',
    label: t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.INBOX.ALL'),
  },
  ...(inboxes.value || []).map(inbox => ({
    value: String(inbox.id),
    label: inbox.name,
  })),
]);
const labelOptions = computed(() =>
  (labels.value || []).map(label => ({
    value: label.title,
    label: label.title,
  }))
);
const isEditingFollowUp = computed(() => !!editingFollowUpRuleId.value);
const isFollowUpFormInvalid = computed(
  () => Number(followUpForm.duration) < 10 || !followUpForm.message.trim()
);

const conversationStatusOptions = computed(() => [
  {
    value: 'open',
    label: t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.STATUSES.OPEN'),
  },
  {
    value: 'pending',
    label: t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.STATUSES.PENDING'),
  },
  {
    value: 'resolved',
    label: t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.STATUSES.RESOLVED'),
  },
  {
    value: 'snoozed',
    label: t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.STATUSES.SNOOZED'),
  },
]);

const serializedRules = computed(() =>
  followUpGroups.value
    .flatMap(group =>
      group.rules.map(rule => ({
        id: rule.id,
        duration: Number(rule.duration),
        message: rule.message?.trim() || '',
        inbox_id: group.inbox_id ? Number(group.inbox_id) : null,
        label: group.labels[0] || null,
        labels: group.labels.length ? [...group.labels] : null,
        exclude_labels: group.exclude_labels.length
          ? [...group.exclude_labels]
          : null,
        include_unlabeled: !!group.include_unlabeled,
        send_csat: !!rule.send_csat,
        enabled: group.enabled !== false,
        conversation_statuses: normalizeStatuses(group.conversation_statuses),
      }))
    )
    .filter(rule => rule.duration >= 10 && rule.message)
);

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
});

const groupRulesByInbox = rules => {
  const groups = rules.reduce((result, rule) => {
    const inboxId = rule.inbox_id ? String(rule.inbox_id) : '';
    const groupKey = [
      inboxId || 'all',
      rule.enabled === false ? 'disabled' : 'enabled',
      normalizeStatuses(rule.conversation_statuses).join('-'),
      normalizeLabels(rule.labels, rule.label).sort().join(',') || 'all-labels',
      normalizeLabels(rule.exclude_labels).sort().join(',') || 'no-exclusions',
      rule.include_unlabeled ? 'with-unlabeled' : 'labeled-only',
    ].join(':');
    const group =
      result.get(groupKey) ||
      createGroup({
        id: `follow-up-group-${groupKey}`,
        inbox_id: inboxId,
        labels: rule.labels,
        label: rule.label,
        exclude_labels: rule.exclude_labels,
        include_unlabeled: rule.include_unlabeled,
        enabled: rule.enabled,
        conversation_statuses: rule.conversation_statuses,
        rules: [],
      });
    group.rules.push(createRule(rule));
    result.set(groupKey, group);
    return result;
  }, new Map());

  return groups.size ? [...groups.values()] : [createGroup()];
};

watch(
  currentAccount,
  () => {
    const { auto_follow_up_rules, auto_follow_up_enabled } =
      currentAccount.value?.settings || {};

    const savedRules = auto_follow_up_rules?.length ? auto_follow_up_rules : [];

    followUpGroups.value = groupRulesByInbox(savedRules);
    isEnabled.value =
      auto_follow_up_enabled === null || auto_follow_up_enabled === undefined
        ? savedRules.length > 0
        : [true, 'true', 1, '1'].includes(auto_follow_up_enabled);
  },
  { deep: true, immediate: true }
);

const addGroup = () => {
  followUpGroups.value.push(createGroup());
};

const removeGroup = groupId => {
  followUpGroups.value = followUpGroups.value.filter(
    group => group.id !== groupId
  );
};

const resetFollowUpForm = (rule = null) => {
  followUpForm.duration = rule?.duration || 10;
  followUpForm.unit = rule?.unit || getDurationUnit(followUpForm.duration);
  followUpForm.message = rule?.message || '';
  followUpForm.send_csat = rule?.send_csat ?? false;
};

const openFollowUpDialog = (groupId, rule = null) => {
  editingFollowUpGroupId.value = groupId;
  editingFollowUpRuleId.value = rule?.id || null;
  resetFollowUpForm(rule);
  followUpDialogRef.value?.open();
};

const closeFollowUpDialog = () => {
  followUpDialogRef.value?.close();
};

const removeRule = (groupId, ruleId) => {
  const group = followUpGroups.value.find(item => item.id === groupId);
  if (!group) return;

  group.rules = group.rules.filter(rule => rule.id !== ruleId);
};

const toggleStatus = (group, status, checked) => {
  if (checked) {
    group.conversation_statuses = normalizeStatuses([
      ...group.conversation_statuses,
      status,
    ]);
    return;
  }

  const nextStatuses = group.conversation_statuses.filter(
    item => item !== status
  );
  group.conversation_statuses = nextStatuses.length ? nextStatuses : ['open'];
};

const isStatusChecked = (group, status) =>
  group.conversation_statuses.includes(status);

const toggleGroupCollapsed = group => {
  group.collapsed = !group.collapsed;
};

const inboxSummary = group =>
  inboxOptions.value.find(option => option.value === group.inbox_id)?.label ||
  t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.INBOX.ALL');

const statusSummary = group => {
  const statuses = normalizeStatuses(group.conversation_statuses);
  if (statuses.length === CONVERSATION_STATUSES.length) {
    return t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.STATUSES.ALL');
  }

  return statuses
    .map(
      status =>
        conversationStatusOptions.value.find(option => option.value === status)
          ?.label
    )
    .filter(Boolean)
    .join(', ');
};

const groupContextSummary = group =>
  `${inboxSummary(group)} · ${statusSummary(group)}`;

const labelSummary = group => {
  const summaries = [];
  if (group.labels.length) {
    summaries.push(
      t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.FILTER_LABEL.SUMMARY', {
        label: group.labels.join(', '),
      })
    );
  } else if (!group.include_unlabeled) {
    summaries.push(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.FILTER_LABEL.ALL'));
  }
  if (group.include_unlabeled) {
    summaries.push(
      t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.FILTER_LABEL.UNLABELED_SUMMARY')
    );
  }
  if (group.exclude_labels.length) {
    summaries.push(
      t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.FILTER_LABEL.EXCLUDE_SUMMARY', {
        label: group.exclude_labels.join(', '),
      })
    );
  }
  return summaries.join(' · ');
};

const followUpDurationLabel = rule => {
  const duration = Number(rule.duration || 0);
  const unit = getDurationUnit(duration);
  if (unit === DURATION_UNITS.DAYS) {
    return t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.SUMMARY_DAYS', {
      count: duration / (24 * 60),
    });
  }
  if (unit === DURATION_UNITS.HOURS) {
    return t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.SUMMARY_HOURS', {
      count: duration / 60,
    });
  }
  return t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.SUMMARY_MINUTES', {
    count: duration,
  });
};

const updateAccountSettings = async settings => {
  try {
    isSubmitting.value = true;
    await updateAccount(settings, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.API.SUCCESS'));
    return true;
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.API.ERROR'));
    return false;
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (!serializedRules.value.length) {
    return updateAccountSettings({
      auto_follow_up_enabled: false,
      auto_follow_up_rules: [],
    });
  }

  return updateAccountSettings({
    auto_follow_up_enabled: true,
    auto_follow_up_rules: serializedRules.value,
  });
};

const saveFollowUp = async () => {
  const group = followUpGroups.value.find(
    item => item.id === editingFollowUpGroupId.value
  );
  if (!group) return;

  const rule = createRule({
    id: editingFollowUpRuleId.value || undefined,
    duration: Number(followUpForm.duration),
    message: followUpForm.message,
    send_csat: followUpForm.send_csat,
  });
  rule.unit = followUpForm.unit;

  if (editingFollowUpRuleId.value) {
    group.rules = group.rules.map(item =>
      item.id === editingFollowUpRuleId.value ? rule : item
    );
  } else {
    group.rules.push(rule);
    editingFollowUpRuleId.value = rule.id;
  }

  const isSaved = await handleSubmit();
  if (isSaved) closeFollowUpDialog();
};

const handleDisable = async () => {
  return updateAccountSettings({
    auto_follow_up_enabled: false,
  });
};

const toggleFollowUp = async () => {
  if (!isEnabled.value) {
    await handleDisable();
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <div class="flex justify-between items-center w-full">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.TITLE') }}
        </h3>
        <div class="flex justify-end">
          <Switch v-model="isEnabled" @change="toggleFollowUp" />
        </div>
      </div>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.NOTE') }}
      </p>
    </div>

    <div v-if="isEnabled" class="px-5 py-4">
      <form class="grid gap-5" @submit.prevent="handleSubmit">
        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.LABEL')"
          :help-message="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.HELP')"
        >
          <div class="grid w-full gap-3">
            <div
              v-for="(group, groupIndex) in followUpGroups"
              :key="group.id"
              class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 transition-colors hover:border-n-strong"
            >
              <div
                class="flex items-stretch justify-between gap-3 bg-n-solid-2"
                :class="{ 'border-b border-n-weak': !group.collapsed }"
              >
                <button
                  type="button"
                  class="flex min-w-0 flex-1 items-center gap-3 px-4 py-4 text-left hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                  :aria-expanded="!group.collapsed"
                  :title="
                    group.collapsed
                      ? t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.EXPAND')
                      : t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.COLLAPSE')
                  "
                  @click="toggleGroupCollapsed(group)"
                >
                  <span
                    class="i-lucide-chevron-down size-5 shrink-0 text-n-slate-10 transition-transform duration-200"
                    :class="{ '-rotate-90': group.collapsed }"
                  />
                  <div class="flex min-w-0 flex-col gap-1">
                    <span
                      class="truncate text-sm font-semibold text-n-slate-12"
                    >
                      {{
                        t(
                          'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.GROUP_TITLE',
                          {
                            number: groupIndex + 1,
                          }
                        )
                      }}
                    </span>
                    <span class="truncate text-xs text-n-slate-11">
                      {{ groupContextSummary(group) }}
                    </span>
                    <span class="truncate text-xs text-n-slate-10">
                      {{ labelSummary(group) }}
                    </span>
                  </div>
                </button>
                <div class="flex shrink-0 items-center gap-3 px-4">
                  <div class="flex items-center gap-2">
                    <span class="text-xs font-medium text-n-slate-11">
                      {{
                        group.enabled
                          ? t(
                              'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.ENABLED'
                            )
                          : t(
                              'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.DISABLED'
                            )
                      }}
                    </span>
                    <Switch v-model="group.enabled" />
                  </div>
                  <NextButton
                    type="button"
                    variant="ghost"
                    color="ruby"
                    icon="i-lucide-trash-2"
                    size="sm"
                    :label="
                      t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.DELETE_INBOX')
                    "
                    @click="removeGroup(group.id)"
                  />
                </div>
              </div>

              <div v-show="!group.collapsed" class="grid gap-4 p-4">
                <WithLabel
                  :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.INBOX.LABEL')"
                  :help-message="
                    t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.INBOX.HELP')
                  "
                >
                  <ComboBox
                    v-model="group.inbox_id"
                    :options="inboxOptions"
                    :placeholder="
                      t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.INBOX.ALL')
                    "
                  />
                </WithLabel>

                <WithLabel
                  :label="
                    t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.FILTER_LABEL.LABEL')
                  "
                  :help-message="
                    t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.FILTER_LABEL.HELP')
                  "
                >
                  <TagMultiSelectComboBox
                    v-model="group.labels"
                    :options="labelOptions"
                    :placeholder="
                      t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.FILTER_LABEL.ALL')
                    "
                  />
                </WithLabel>

                <WithLabel
                  :label="
                    t(
                      'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.FILTER_LABEL.EXCLUDE_LABEL'
                    )
                  "
                  :help-message="
                    t(
                      'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.FILTER_LABEL.EXCLUDE_HELP'
                    )
                  "
                >
                  <TagMultiSelectComboBox
                    v-model="group.exclude_labels"
                    :options="labelOptions"
                    :placeholder="
                      t(
                        'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.FILTER_LABEL.EXCLUDE_PLACEHOLDER'
                      )
                    "
                  />
                </WithLabel>

                <div
                  class="flex items-center justify-between rounded-lg border border-n-weak bg-n-solid-2 px-4 py-3"
                >
                  <div class="flex flex-col gap-0.5">
                    <span class="text-sm font-medium text-n-slate-12">
                      {{
                        t(
                          'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.FILTER_LABEL.UNLABELED_LABEL'
                        )
                      }}
                    </span>
                    <span class="text-xs text-n-slate-11">
                      {{
                        t(
                          'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.FILTER_LABEL.UNLABELED_HELP'
                        )
                      }}
                    </span>
                  </div>
                  <Switch v-model="group.include_unlabeled" />
                </div>

                <WithLabel
                  :label="
                    t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.STATUSES.LABEL')
                  "
                  :help-message="
                    t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.STATUSES.HELP')
                  "
                >
                  <div class="follow-up-status-grid">
                    <label
                      v-for="status in conversationStatusOptions"
                      :key="status.value"
                      class="follow-up-status-option"
                    >
                      <Checkbox
                        :model-value="isStatusChecked(group, status.value)"
                        @change="
                          event =>
                            toggleStatus(
                              group,
                              status.value,
                              event.target.checked
                            )
                        "
                      />
                      <span>{{ status.label }}</span>
                    </label>
                  </div>
                </WithLabel>

                <div class="grid gap-3">
                  <div
                    v-if="!group.rules.length"
                    class="rounded-lg border border-dashed border-n-weak bg-n-solid-2 p-4 text-sm text-n-slate-11"
                  >
                    {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.EMPTY') }}
                  </div>
                  <div v-else class="grid gap-2">
                    <div
                      v-for="(rule, ruleIndex) in group.rules"
                      :key="rule.id"
                      class="follow-up-rule-row"
                    >
                      <div class="min-w-0">
                        <div class="flex flex-wrap items-center gap-2">
                          <span class="text-sm font-medium text-n-slate-12">
                            {{
                              t(
                                'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.ITEM_TITLE',
                                { number: ruleIndex + 1 }
                              )
                            }}
                          </span>
                          <span class="follow-up-rule-duration">
                            {{ followUpDurationLabel(rule) }}
                          </span>
                        </div>
                        <p class="mb-0 mt-1 truncate text-sm text-n-slate-11">
                          {{ rule.message }}
                        </p>
                      </div>

                      <div class="follow-up-rule-actions">
                        <NextButton
                          type="button"
                          variant="faded"
                          color="slate"
                          icon="i-lucide-pencil"
                          size="sm"
                          :label="
                            t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.EDIT')
                          "
                          @click="openFollowUpDialog(group.id, rule)"
                        />
                        <NextButton
                          type="button"
                          variant="ghost"
                          color="ruby"
                          icon="i-lucide-trash-2"
                          size="sm"
                          :label="
                            t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.DELETE')
                          "
                          @click="removeRule(group.id, rule.id)"
                        />
                      </div>
                    </div>
                  </div>

                  <NextButton
                    type="button"
                    variant="faded"
                    color="slate"
                    icon="i-lucide-plus"
                    :label="
                      t(
                        'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.ADD_FOLLOW_UP'
                      )
                    "
                    class="w-fit"
                    @click="openFollowUpDialog(group.id)"
                  />
                </div>
              </div>
            </div>
            <NextButton
              type="button"
              variant="faded"
              color="slate"
              icon="i-lucide-plus"
              :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.ADD_INBOX')"
              class="w-fit"
              @click="addGroup"
            />
          </div>
        </WithLabel>

        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.UPDATE_BUTTON')"
          />
        </div>
      </form>
    </div>

    <Dialog
      ref="followUpDialogRef"
      width="2xl"
      overflow-y-auto
      :title="
        isEditingFollowUp
          ? t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.EDIT_MODAL_TITLE')
          : t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.NEW_MODAL_TITLE')
      "
      :confirm-button-label="
        t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.RULES.SAVE_FOLLOW_UP')
      "
      :disable-confirm-button="isFollowUpFormInvalid"
      :is-loading="isSubmitting"
      @confirm="saveFollowUp"
    >
      <div class="grid gap-4">
        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.LABEL')"
          :help-message="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.HELP')"
        >
          <div
            class="grid w-full grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,0.45fr)] [&>input]:w-full [&>select]:w-full"
          >
            <DurationInput
              v-model="followUpForm.duration"
              v-model:unit="followUpForm.unit"
              min="0"
              max="1438560"
            />
          </div>
        </WithLabel>

        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.LABEL')"
          :help-message="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.HELP')"
        >
          <TextArea
            v-model="followUpForm.message"
            class="w-full"
            :placeholder="
              t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.PLACEHOLDER')
            "
          />
        </WithLabel>

        <div
          class="flex items-center justify-between rounded-lg border border-n-weak bg-n-solid-2 px-4 py-3"
        >
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.CSAT.LABEL') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.CSAT.HELP') }}
            </span>
          </div>
          <Switch v-model="followUpForm.send_csat" />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
.follow-up-rule-row {
  @apply grid min-w-0 gap-3 rounded-lg border border-n-weak bg-n-solid-2 p-3;
}

@media (min-width: 1280px) {
  .follow-up-rule-row {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
  }
}

.follow-up-rule-actions {
  @apply flex min-w-0 flex-wrap gap-2;
}

@media (min-width: 1280px) {
  .follow-up-rule-actions {
    @apply justify-end;
  }
}

.follow-up-rule-duration {
  @apply rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11;
}

.follow-up-status-grid {
  @apply grid w-full gap-2 sm:grid-cols-2 xl:grid-cols-4;
}

.follow-up-status-option {
  @apply flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12;
}
</style>
